import React, { useState, useCallback, useEffect } from 'react';
import { devLog } from '../lib/log';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useDataCache, useCache } from '../contexts/DataCacheContext';
import { getAthleteStats, getActiveProgramWithWorkouts, getNextSuggestedWorkout, getAthleteWeeklyVolume, getAthletePrograms } from '../lib/database';
import { STARTER_PROGRAMS, createStarterProgram } from '../lib/starterPrograms';
import SkeletonLoader from './SkeletonLoader';
import BottomNav from './BottomNav';
import WorkoutCompletePopup from './WorkoutCompletePopup';
import './AthleteDashboard.css';

function AthleteDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { signOut, profile } = useAuth();
  const toast = useToast();
  const { invalidate: invalidateCache } = useCache();
  const [isVolumeExpanded, setIsVolumeExpanded] = useState(false);
  const [xpPopup, setXpPopup] = useState(null);

  // Onboarding nell'empty state: 'choices' -> 'days' -> 'confirm'.
  // Nessuna persistenza: lo stato è derivato dai dati (finché non esiste una
  // scheda attiva, la dashboard è l'onboarding stesso).
  const [onboardingStep, setOnboardingStep] = useState('choices');
  const [starterDays, setStarterDays] = useState(3);
  const [creatingStarter, setCreatingStarter] = useState(false);

  const handleCreateStarter = async () => {
    if (creatingStarter) return; // anti doppio-tap
    setCreatingStarter(true);
    try {
      await createStarterProgram(profile.id, starterDays);
      invalidateCache(`athlete-dashboard-${profile.id}`);
      // Ricarica pulita: la dashboard rilegge e mostra la scheda attiva.
      window.location.assign('/athlete');
    } catch (e) {
      console.error('Starter creation failed:', e.message);
      toast.error('Non sono riuscito ad attivare la scheda. La trovi in "Le tue Schede" se è stata creata.');
      // La creazione può essere riuscita in parte (es. attivazione fallita):
      // ricarica i dati così, se la scheda esiste, il gate isBrandNewUser
      // diventa falso e si mostra l'empty state operativo (niente onboarding
      // riproposto → niente starter duplicate).
      invalidateCache(`athlete-dashboard-${profile.id}`);
      refreshDashboard();
      setCreatingStarter(false);
    }
  };

  useEffect(() => {
    // Il popup di fine workout compare SEMPRE al completamento, anche quando
    // l'assegnazione XP è fallita (xpResult null): le statistiche del
    // riepilogo non dipendono dagli XP.
    if (location.state?.workoutCompleted || location.state?.xpResult) {
      setXpPopup({
        xpResult: location.state.xpResult || null,
        summary: location.state.workoutSummary || null,
        newBadges: location.state.newBadges || [],
        workoutName: location.state.workoutName || '',
      });
      // Clear from history so popup doesn't reappear on back-navigation
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch all dashboard data in a single atomic call
  const fetchDashboardData = useCallback(async () => {
    if (!profile?.id) {
      /*#__PURE__*/ devLog('[DASHBOARD] No profile.id, returning null');
      return null;
    }
    const t0 = Date.now();
    /*#__PURE__*/ devLog('[DASHBOARD] Starting fetchDashboardData for:', profile.id);

    // TRUE Parallel fetch: stats + program+workouts + volume (Promise.all for max performance)
    /*#__PURE__*/ devLog('[DASHBOARD] Fetching all data in parallel...');

    const [statsData, programData, volumeData, myPrograms] = await Promise.all([
      getAthleteStats(profile.id).catch(e => {
        console.error('[DASHBOARD] Stats fetch failed:', e.message);
        return { totalWorkouts: 0, monthWorkouts: 0, streak: 0 };
      }),
      getActiveProgramWithWorkouts(profile.id).catch(e => {
        console.error('[DASHBOARD] Program fetch failed:', e.message);
        return null;
      }),
      getAthleteWeeklyVolume(profile.id).catch(e => {
        console.error('[DASHBOARD] Volume fetch failed:', e.message);
        return {};
      }),
      // Serve solo a distinguere utente NUOVO (onboarding) da utente senza
      // scheda attiva ma con storia (empty state operativo). In dubbio (errore)
      // si assume che le schede esistano: meglio non mostrare l'onboarding.
      getAthletePrograms(profile.id).catch(() => [{}])
    ]);

    /*#__PURE__*/ devLog('[DASHBOARD] All parallel fetches completed');
    /*#__PURE__*/ devLog('[DASHBOARD] Stats:', statsData);
    /*#__PURE__*/ devLog('[DASHBOARD] Program:', programData ? 'found' : 'null');
    /*#__PURE__*/ devLog('[DASHBOARD] Volume: OK');

    // Build active program + next workout (1 extra fetch, but only if program exists)
    let activeProgram = null;
    let nextWorkout = null;
    let workoutContext = null;

    if (programData?.program) {
      /*#__PURE__*/ devLog('[DASHBOARD] Found active program:', programData.program.id, programData.program.name);
      activeProgram = { program: programData.program };
      if (programData.workouts?.length > 0) {
        /*#__PURE__*/ devLog('[DASHBOARD] Found', programData.workouts.length, 'workouts, getting suggested...');
        try {
          const result = await getNextSuggestedWorkout(profile.id, programData.program.id);
          /*#__PURE__*/ devLog('[DASHBOARD] Suggested workout result:', result);
          const suggestedId = typeof result === 'string' ? result : result?.suggestedId;
          const context = typeof result === 'object' ? result?.context : null;
          nextWorkout = programData.workouts.find(w => w.id === suggestedId) || null;
          workoutContext = context;
          /*#__PURE__*/ devLog('[DASHBOARD] Next workout:', nextWorkout?.name || 'null');
        } catch (e) {
          console.error('[DASHBOARD] Error getting next suggested workout:', e.message);
        }
      }
    } else {
      /*#__PURE__*/ devLog('[DASHBOARD] No active program found');
    }

    /*#__PURE__*/ devLog('[DASHBOARD] All data loaded in ', Date.now() - t0, 'ms');
    return { stats: statsData, activeProgram, nextWorkout, workoutContext, weeklyVolume: volumeData, hasPrograms: (myPrograms || []).length > 0 };
  }, [profile?.id]);

  const { data, loading, refresh: refreshDashboard } = useDataCache(
    profile?.id ? `athlete-dashboard-${profile.id}` : null,
    fetchDashboardData,
    60000
  );

  // Destructure cached data with defaults
  const stats = data?.stats || { totalWorkouts: 0, monthWorkouts: 0, streak: 0 };
  const activeProgram = data?.activeProgram || null;
  const nextWorkout = data?.nextWorkout || null;
  const workoutContext = data?.workoutContext || null;
  const weeklyVolume = data?.weeklyVolume || {};

  // Onboarding con starter SOLO per utenti realmente nuovi e vuoti:
  // zero allenamenti (anche liberi), zero schede (proprie o assegnate, anche
  // disattivate), non coach. Tutti gli altri senza scheda attiva vedono
  // l'empty state operativo classico.
  const isBrandNewUser =
    (stats.totalWorkouts || 0) === 0 &&
    !data?.hasPrograms &&
    !profile?.coach_mode &&
    profile?.role !== 'coach';

  const handleLogout = async () => {
    await signOut();
    navigate('/');
  };

  const handleStartWorkout = () => {
    if (nextWorkout?.id) {
      navigate(`/athlete/workout/${nextWorkout.id}`);
    } else if (activeProgram?.program) {
      navigate('/athlete/program');
    }
  };

  const handleViewProgram = () => {
    navigate('/athlete/program');
  };

  const handleViewHistory = () => {
    navigate('/athlete/history');
  };

  if (loading) {
    return (
      <div className="dashboard-container athlete-dashboard page-with-bottom-nav">
        <header className="athlete-header">
          <div className="header-content">
            <h1 className="greeting">Ciao {profile?.name || 'Atleta'}!</h1>
          </div>
        </header>
        <main className="athlete-main">
          <SkeletonLoader variant="dashboard" />
        </main>
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="dashboard-container athlete-dashboard page-with-bottom-nav">
      <header className="athlete-header">
        <div className="header-content">
          <h1 className="greeting">Ciao {profile?.name || 'Atleta'}!</h1>
          <button className="btn-logout" onClick={handleLogout}>
            Esci
          </button>
        </div>
      </header>

      <main className="athlete-main">
        {activeProgram?.program ? (
          <>
            {/* ========================================
                HERO SECTION - Flusso principale
                Streak → Prossimo allenamento → CTA
               ======================================== */}
            <div className="hero-section">
              {/* Streak badge integrato - doppia metrica */}
              {(stats.streak > 0 || stats.cyclesCompleted > 0) && (
                <div className="hero-streak">
                  {stats.streak > 0 && (
                    <>
                      <span className="hero-streak-icon">🔥</span>
                      <span className="hero-streak-text">
                        {stats.streak} {stats.streak === 1 ? 'giorno' : 'giorni'}
                      </span>
                    </>
                  )}
                  {stats.cyclesCompleted > 0 && (
                    <>
                      {stats.streak > 0 && <span className="hero-streak-separator">·</span>}
                      <span className="hero-streak-icon">📊</span>
                      <span className="hero-streak-text">
                        {stats.cyclesCompleted} {stats.cyclesCompleted === 1 ? 'ciclo' : 'cicli'}
                      </span>
                    </>
                  )}
                </div>
              )}

              {/* Contesto: cosa devi fare oggi */}
              <div className="hero-context">
                {workoutContext?.lastCompletedDayNumber && (
                  <p className="hero-last-completed">
                    Ultimo completato: <strong>Giorno {workoutContext.lastCompletedDayNumber}</strong>
                    {workoutContext.lastCompletedDate && (() => {
                      try {
                        const date = new Date(workoutContext.lastCompletedDate);
                        if (isNaN(date.getTime())) return null;
                        return (
                          <span className="hero-last-date">
                            {' '}({new Intl.DateTimeFormat('it-IT', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }).format(date)})
                          </span>
                        );
                      } catch (e) {
                        console.error('Date format error:', e);
                        return null;
                      }
                    })()}
                  </p>
                )}
                <p className="hero-label">
                  {workoutContext?.isNewCycle ? 'Nuovo ciclo — ricomincia da' : 'Il tuo prossimo allenamento'}
                </p>
                {nextWorkout ? (
                  <h2 className="hero-workout-name">
                    <span className="hero-day-badge">Giorno {nextWorkout.day_number}</span>
                    {nextWorkout.name}
                  </h2>
                ) : (
                  <h2 className="hero-workout-name">{activeProgram.program.name}</h2>
                )}
                {nextWorkout?.description && (
                  <p className="hero-description">{nextWorkout.description}</p>
                )}
              </div>

              {/* CTA Principale */}
              <button className="hero-cta" onClick={handleStartWorkout}>
                Inizia Allenamento
              </button>
            </div>

            {/* ========================================
                AZIONI SECONDARIE
               ======================================== */}
            <div className="secondary-actions">
              {/* Vedi Scheda - Secondaria ma accessibile */}
              <button className="btn-secondary" onClick={handleViewProgram}>
                <span className="btn-secondary-icon">📋</span>
                <span>Vedi Scheda Attiva</span>
              </button>

              {/* Storico - Informativo */}
              <button className="btn-tertiary" onClick={handleViewHistory}>
                <span className="btn-tertiary-icon">📊</span>
                <span>Storico Allenamenti</span>
              </button>
            </div>

            {/* ========================================
                OPZIONE ALTERNATIVA - Allenamento Libero
               ======================================== */}
            <div className="alternative-action">
              <button
                className="btn-alternative"
                onClick={() => navigate('/athlete/free-workout')}
              >
                oppure fai un <strong>allenamento libero</strong>
              </button>
            </div>
          </>
        ) : !isBrandNewUser ? (
          /* ========================================
             EMPTY STATE operativo - Nessuna scheda attiva
             (utente con storia: schede disattivate, allenamenti
             liberi o attività coach — niente onboarding)
            ======================================== */
          <div className="empty-state">
            <div className="empty-state-icon">📋</div>
            <h2 className="empty-state-title">Nessuna scheda attiva</h2>
            <p className="empty-state-message">
              Crea la tua prima scheda oppure fatti seguire da un personal trainer.
            </p>
            <button
              className="btn-free-workout-primary"
              onClick={() => navigate('/athlete/free-workout')}
            >
              🏋️ Allenamento Libero
            </button>
            <button
              className="btn-create-first-program"
              onClick={() => navigate('/athlete/programs')}
            >
              📋 Le tue Schede
            </button>
            <button
              className="btn-create-first-program"
              onClick={() => navigate('/athlete/programs/new')}
            >
              ➕ Crea nuova scheda
            </button>
            <button
              className="btn-view-history-secondary"
              onClick={handleViewHistory}
            >
              📊 Vedi storico allenamenti
            </button>
          </div>
        ) : (
          /* ========================================
             ONBOARDING - utente nuovo senza alcuna attività
            ======================================== */
          <div className="empty-state">
            {onboardingStep === 'choices' && (
              <>
                <div className="empty-state-icon">👋</div>
                <h2 className="empty-state-title">Benvenuto su Uptra{profile?.name ? `, ${profile.name.split(' ')[0]}` : ''}!</h2>
                <p className="empty-state-message">
                  Da dove vuoi partire?
                </p>
                <button
                  className="btn-create-first-program onboarding-primary"
                  onClick={() => setOnboardingStep('days')}
                >
                  ⭐ Inizia con una scheda d'esempio
                  <span className="onboarding-hint">Una full body semplice, pronta in un tocco. È un punto di partenza: potrai modificare ogni esercizio.</span>
                </button>
                <button
                  className="btn-create-first-program"
                  onClick={() => navigate('/athlete/programs/new')}
                >
                  ✏️ Crea la tua scheda da zero
                  <span className="onboarding-hint">Hai già in mente il tuo programma? Costruiscilo tu.</span>
                </button>
                <button
                  className="btn-free-workout-primary"
                  onClick={() => navigate('/athlete/free-workout')}
                >
                  🏋️ Fai un allenamento libero
                  <span className="onboarding-hint">Niente schede: registra quello che fai oggi.</span>
                </button>
                <p className="onboarding-coach-note">
                  Ti segue un personal trainer? Chiedigli di aggiungerti come atleta: vedrai qui la sua scheda.
                </p>
                <button
                  className="btn-view-history-secondary"
                  onClick={handleViewHistory}
                >
                  📊 Vedi storico allenamenti
                </button>
              </>
            )}

            {onboardingStep === 'days' && (
              <>
                <div className="empty-state-icon">🗓️</div>
                <h2 className="empty-state-title">Quanti giorni a settimana pensi di allenarti?</h2>
                <p className="empty-state-message">
                  Nessun vincolo: puoi cambiare tutto in qualsiasi momento.
                </p>
                <button
                  className="btn-create-first-program"
                  onClick={() => { setStarterDays(2); setOnboardingStep('confirm'); }}
                >
                  2 giorni
                </button>
                <button
                  className="btn-create-first-program"
                  onClick={() => { setStarterDays(3); setOnboardingStep('confirm'); }}
                >
                  3 giorni
                </button>
                <button
                  className="btn-view-history-secondary"
                  onClick={() => setOnboardingStep('choices')}
                >
                  ← Indietro
                </button>
              </>
            )}

            {onboardingStep === 'confirm' && (
              <>
                <h2 className="empty-state-title">{STARTER_PROGRAMS[starterDays].name}</h2>
                <p className="empty-state-message">
                  Questa scheda è un esempio generico, non un programma personalizzato.
                  Modifica esercizi, serie e carichi in base a come ti alleni.
                </p>
                <div className="starter-preview">
                  {STARTER_PROGRAMS[starterDays].days.map((day) => (
                    <div key={day.name} className="starter-preview-day">
                      <h3>{day.name}</h3>
                      <ul>
                        {day.exercises.map((ex) => (
                          <li key={ex.name}>
                            <span>{ex.name}</span>
                            <span className="starter-preview-sets">{ex.sets}×{ex.reps}{ex.name === 'Plank' ? '"' : ''}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
                <button
                  className="btn-create-first-program onboarding-primary"
                  onClick={handleCreateStarter}
                  disabled={creatingStarter}
                >
                  {creatingStarter ? 'Creo la scheda…' : '✓ Crea e attiva'}
                </button>
                <button
                  className="btn-view-history-secondary"
                  onClick={() => setOnboardingStep('days')}
                  disabled={creatingStarter}
                >
                  ← Indietro
                </button>
              </>
            )}
          </div>
        )}
      </main>

      <BottomNav />

      {xpPopup && (
        <WorkoutCompletePopup
          xpResult={xpPopup.xpResult}
          summary={xpPopup.summary}
          newBadges={xpPopup.newBadges}
          workoutName={xpPopup.workoutName}
          athleteId={profile?.id}
          onClose={() => setXpPopup(null)}
        />
      )}
    </div>
  );
}

export default AthleteDashboard;
